//  Material
import { useMemo } from 'react'
import { Divider } from '@mui/material'
// Logic
import { HallType } from '@weblite/ts-action/hall'
import { Subscriber } from '@weblite/ts-action/subscription'
import {
  useClientUserPermissions,
  useHallSubscribers,
  useSearchedSubscribers,
} from '$logic/subscription/react-hook'
// Common
import { MenuIcon } from '$common/icons/menu'
import { UserListItem, UserListItemSkeleton } from '$common/user-list-item'
import { EmptyStateList } from '$common/empty-state-list'
import { withSearchableInfiniteListPage } from '$common/searchable-list-page'
// Localization
import { t } from '$i18n'
import { useRoom } from '$logic/room/react-hook'
import {
  closeHallSubscribers,
  openAddHallMembers,
  openUserProfile,
} from '$logic/router'
import { showHallMemberMenu } from '$comp/modals/menus/hall-member-menu'
import { getClientUserId } from '$logic/client-user'
import { UserStatus } from '$common/user-status'
// Components
import { MemberJoiningDateStatus } from './member-joining-date-status'

const ListItem = ({
  userId,
  isAdmin,
  isOwner,
  hallId,
  showSecondaryAction,
  joinedAt,
  hallType,
}: {
  userId: string
  hallId: string
  isAdmin: boolean
  isOwner: boolean
  showSecondaryAction: boolean
  joinedAt: number
  hallType: HallType
}) => (
  <>
    <UserListItem
      userId={userId}
      hasDivider={false}
      onClick={() => openUserProfile(userId)}
      role={isOwner ? t('common.owner') : isAdmin ? t('common.admin') : null}
      secondaryText={
        hallType === 'GROUP' ? (
          <UserStatus userId={userId} />
        ) : (
          <MemberJoiningDateStatus joinedAt={joinedAt} />
        )
      }
      SecondaryActionIcon={showSecondaryAction ? MenuIcon : undefined}
      onSecondaryActionIconClick={() =>
        showHallMemberMenu({ userId, hallId, isAdmin })
      }
    />
    <Divider variant="inset" />
  </>
)

export function MemberList({ roomId }: { roomId: string }) {
  const room = useRoom(roomId)
  const clientUserPermissions = useClientUserPermissions(roomId)

  const Page = useMemo(
    () =>
      withSearchableInfiniteListPage<
        Subscriber,
        string,
        { roomId: string; query: string; fetchedItems: Subscriber[] }
      >({
        appBarTitle: t(
          `profile.hall.actions.${
            room?.type === 'GROUP' ? 'members' : 'subscribers'
          }`,
        ),
        SkeletonItem: UserListItemSkeleton,
        EmptyStateComponent: () => <EmptyStateList />,
        fetchHook: useHallSubscribers,
        searchHook: useSearchedSubscribers,
        onActionButtonClick: () => openAddHallMembers(roomId, false),
        onBack: closeHallSubscribers,
      }),
    [roomId, room?.type],
  )

  return (
    <Page
      fetchHookParams={roomId}
      getSearchHookParams={(query, fetchedItems) => ({
        roomId,
        query,
        fetchedItems,
      })}
      ListItem={item => (
        <ListItem
          hallType={room?.type as HallType}
          userId={item.id}
          joinedAt={item.createdAt}
          hallId={roomId}
          isAdmin={!!item.admin}
          isOwner={!!item.owner}
          // NOTE: If selected user is not owner or not client user and client user is owner or has the permission for removing users or adding admins.
          showSecondaryAction={
            !(
              item.owner ||
              (item.admin &&
                !clientUserPermissions?.isOwner &&
                clientUserPermissions?.isAdmin) ||
              getClientUserId() === item.id
            ) &&
            (clientUserPermissions?.isOwner ||
              clientUserPermissions?.banUsers ||
              clientUserPermissions?.addNewAdmins)
          }
        />
      )}
      hideActionButton={!clientUserPermissions?.addMembers}
    />
  )
}
